/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import { Currency, Price, useCurrencies } from '@zapier/currency';
import { useQuery } from '@apollo/react-hooks';

import { Colors } from '@zapier/checkout/src/constants';
import { Interval } from '@zapier/checkout/src/types';

export type InvoiceLineItem = {
  amount: number;
  currency?: string;
  description?: string;
  id: string;
};

export type InvoiceLineSubscription = {
  amount: number;
  currency?: string;
  id: string;
  plan?: {
    interval: Interval;
    name: string;
  };
};

type Props = {
  line: InvoiceLineItem | InvoiceLineSubscription;
};

const itemCss = css`
  color: ${Colors.batmobile};
  display: flex;

  & + & {
    margin-top: 10px;

    @media (min-width: 750px) {
      margin-top: 10px;
    }
  }
`;

const descriptionCss = css`
  flex: 1 0 0;
  padding-right: 20px;
`;

const priceCss = (isRefund: boolean) => css`
  color: ${isRefund ? Colors.viper : 'inherit'};
  flex: 0 0 90px;
  text-align: right;
  white-space: nowrap;
`;

export const InvoiceLine = ({ line }: Props) => {
  const { currencies } = useCurrencies({ useQuery });
  // Use the line item ISO code to get symbol etc from the currenciesMap
  const currency = currencies.find((c: Currency) => c.name === line.currency);

  // for the Backend, a negative price means a refund...
  const isRefund = line.amount < 0;

  // some invoice items dont come with a description (e.g. subscription items)
  let description;
  if ('description' in line) {
    description = line.description;
  } else if ('plan' in line) {
    description = line.plan && `${line.plan.name} (${line.plan.interval}ly)`;
  }

  return (
    <div css={itemCss}>
      <span css={descriptionCss}>{description}</span>
      <span css={priceCss(isRefund)}>
        <Price currency={currency} isLocalized={true}>
          {line.amount}
        </Price>
      </span>
    </div>
  );
};
